// Read + write hooks for the admin Users screen.
//
// Reads (access list, audit feed) go through the Data Connect client SDK
// (admin-claim enforced server-side). Writes go through the privileged admin-api
// (also admin-claim enforced). On success each mutation invalidates the access
// list (and the audit feed) so the screen refreshes; errors surface a toast and
// stay readable via the hook's `error`.
import { useMutation, useQuery } from '@tanstack/react-query';
import { queryClient } from '../lib/queryClient';
import { queryKeys } from '../lib/queryKeys';
import { useToast } from '../components/ui/Toast';
import { fetchAccessGrants, fetchAccessAuditLog, fetchNameDirectory, type AccessGrantRecord } from '../lib/db';
import { buildNameDirectory, type NameDirectory } from '../lib/people';
import {
  adminApi,
  type GrantInput,
  type CreateCredentialsInput,
  type CreateCredentialsResult,
  type SetPasswordInput,
  type SetPasswordResult,
} from '../lib/adminApi';

/** The access list for the admin Users table. */
export function useAccessGrants() {
  return useQuery({ queryKey: queryKeys.accessGrants, queryFn: fetchAccessGrants });
}

/**
 * The active-user name directory (email → name parts), for resolving assignee /
 * reviewer emails to "Lindiwe T." on the Transactions + Working Papers surfaces.
 * USER-level read, available to reviewers and admins alike. Cached for 5 minutes
 * (names change rarely) and returned pre-indexed as a Map for O(1) lookup.
 */
export function useNameDirectory() {
  return useQuery({
    queryKey: queryKeys.nameDirectory,
    queryFn: fetchNameDirectory,
    select: (rows): NameDirectory => buildNameDirectory(rows),
    staleTime: 5 * 60 * 1000,
  });
}

/** The recent access audit feed for the activity panel. */
export function useAccessAuditLog() {
  return useQuery({ queryKey: queryKeys.accessAuditLog, queryFn: fetchAccessAuditLog });
}

/** Invalidate the two caches the Users screen reads. */
function invalidateAccess(): void {
  void queryClient.invalidateQueries({ queryKey: queryKeys.accessGrants });
  void queryClient.invalidateQueries({ queryKey: queryKeys.accessAuditLog });
}

export function useGrantAccess() {
  const { show } = useToast();
  return useMutation({
    mutationFn: (input: GrantInput) => adminApi.grant(input),
    onSuccess: () => {
      invalidateAccess();
      show('Access granted.', 'success');
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to grant access.', 'error');
    },
  });
}

/** Set a person's notification switches (admin-only — the Notifications screen). */
export function useSetNotificationPrefs() {
  const { show } = useToast();
  return useMutation({
    mutationFn: (input: { email: string; notifyInApp: boolean; notifyEmail: boolean }) =>
      adminApi.setNotificationPrefs(input.email, {
        notifyInApp: input.notifyInApp,
        notifyEmail: input.notifyEmail,
      }),
    onSuccess: (_data, variables) => {
      // Patch the cached row with the values the server just CONFIRMED, and do
      // NOT invalidate the access list here. The old code refetched immediately;
      // under read-after-write lag that refetch returned the pre-write row and
      // overwrote the patch, so the tick box snapped straight back — the "the
      // switches don't save" report (owner feedback Aug 2026; the writes were in
      // fact persisting, the UI just reverted them). Only the audit feed refreshes.
      queryClient.setQueryData<AccessGrantRecord[]>(queryKeys.accessGrants, (rows) =>
        rows?.map((r) =>
          r.email === variables.email
            ? { ...r, notifyInApp: variables.notifyInApp, notifyEmail: variables.notifyEmail }
            : r,
        ),
      );
      void queryClient.invalidateQueries({ queryKey: queryKeys.accessAuditLog });
      show('Notification settings saved.', 'success');
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to update notification settings.', 'error');
    },
  });
}

export function useRevokeAccess() {
  const { show } = useToast();
  return useMutation({
    mutationFn: (email: string) => adminApi.revoke(email),
    onSuccess: () => {
      invalidateAccess();
      show('Access revoked.', 'success');
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to revoke access.', 'error');
    },
  });
}

export function useCreateCredentials() {
  const { show } = useToast();
  return useMutation<CreateCredentialsResult, unknown, CreateCredentialsInput>({
    mutationFn: (input: CreateCredentialsInput) => adminApi.createCredentials(input),
    onSuccess: () => {
      invalidateAccess();
      show('Account created.', 'success');
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to create the account.', 'error');
    },
  });
}

/** Set or reset an existing user's sign-in password (idempotent — provisions the
 *  account if it doesn't exist yet). The admin conveys the returned password. */
export function useSetPassword() {
  const { show } = useToast();
  return useMutation<SetPasswordResult, unknown, SetPasswordInput>({
    mutationFn: (input: SetPasswordInput) => adminApi.setPassword(input),
    onSuccess: () => {
      invalidateAccess();
      show('Password updated.', 'success');
    },
    onError: (error: unknown) => {
      show(error instanceof Error ? error.message : 'Failed to set the password.', 'error');
    },
  });
}
